import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../contexts/AuthProvider";
import { useFormatPersonName } from "../../../hooks/useNameFormat";
import type { CustomPrintoutTurnAuthor } from "./customPrintoutTypes";

/**
 * Who asked for a saved turn, or saved a version, as the chat and the version history name them: "You" for the viewer,
 * a colleague's name in the department's name order, or "Former team member" once the account is gone.
 */
export const useCustomPrintoutAuthorLabel = () => {
	const { t } = useTranslation();
	const { userInfo } = useAuth();
	const formatPersonName = useFormatPersonName();
	return useCallback(
		(author: CustomPrintoutTurnAuthor | null): string => {
			if (author === null) return t("printout.custom.ai.turn.author.unknown");
			if (author.userId === userInfo.id) return t("printout.custom.ai.turn.author.you");
			// DEFAULT: an admin with no staff record has only a first name; an empty last name leaves just that (the formatter trims).
			return formatPersonName({ firstName: author.firstName, lastName: author.lastName ?? "" });
		},
		[t, userInfo.id, formatPersonName],
	);
};
